/**
 * DecisionFlow — Forgot Password
 * ui/src/screens/ForgotPassword.jsx
 * data-fit LLC · S664 · July 31, 2026
 *
 * Public route: /forgot-password
 * No auth required. Calls POST /api/v1/auth/request-password-reset.
 * Always shows success message — never reveals account existence.
 *
 * spec_auth_identity_s661.md §3.3 — forgot password flow
 */

import { useState } from "react";
import { useNavigate } from "react-router-dom";

const API_BASE = "/api/v1";

export default function ForgotPassword() {
  const [email,       setEmail]       = useState("");
  const [tenantSlug,  setTenantSlug]  = useState("");
  const [submitting,  setSubmitting]  = useState(false);
  const [submitted,   setSubmitted]   = useState(false);

  const navigate = useNavigate();

  async function handleSubmit(e) {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);

    try {
      await fetch(`${API_BASE}/auth/request-password-reset`, {
        method:  "POST",
        headers: { "Content-Type": "application/json" },
        body:    JSON.stringify({
          email:       email.trim(),
          tenant_slug: tenantSlug.trim(),
        }),
      });
      // Always show success — endpoint is always 200, never reveals account existence
    } catch (_) {
      // Silent — same UX on network failure: show success to avoid enumeration
    } finally {
      setSubmitted(true);
      setSubmitting(false);
    }
  }

  // ── Scoped CSS ──────────────────────────────────────────────────────────────
  const css = `
    .dfp-root {
      display: flex;
      align-items: center;
      justify-content: center;
      min-height: 100vh;
      background: rgb(var(--color-bg));
      font-family: "IBM Plex Sans", "Segoe UI", Arial, sans-serif;
      padding: 24px;
    }

    .dfp-card {
      width: 100%;
      max-width: 420px;
      background: rgb(var(--color-surface));
      border: 1px solid rgb(var(--color-border));
      border-radius: 14px;
      padding: 40px 40px 36px;
      box-shadow: 0 4px 24px rgba(0,0,0,0.06);
    }

    /* Product identity */
    .dfp-identity {
      margin-bottom: 28px;
    }
    .dfp-identity-brand {
      font-size: var(--font-sz-xs);
      font-weight: 500;
      color: rgb(var(--color-text-muted));
      letter-spacing: 0.04em;
      margin-bottom: 3px;
    }
    .dfp-identity-name {
      font-size: var(--font-sz-xl);
      font-weight: 700;
      color: rgb(var(--color-primary));
      letter-spacing: -0.01em;
      line-height: 1.1;
    }

    /* Header */
    .dfp-header {
      margin-bottom: 24px;
    }
    .dfp-title {
      font-size: var(--font-sz-lg);
      font-weight: 700;
      color: rgb(var(--color-text));
      margin-bottom: 6px;
    }
    .dfp-subtitle {
      font-size: var(--font-sz-sm);
      color: rgb(var(--color-text-muted));
      line-height: 1.5;
    }

    /* Fields */
    .dfp-field {
      margin-bottom: 18px;
    }
    .dfp-label {
      display: block;
      font-size: var(--font-sz-sm);
      font-weight: 500;
      color: rgb(var(--color-text));
      margin-bottom: 6px;
    }
    .dfp-input {
      width: 100%;
      padding: 10px 13px;
      border: 1px solid rgb(var(--color-border));
      border-radius: 7px;
      background: rgb(var(--color-surface));
      color: rgb(var(--color-text));
      font-size: var(--font-sz-sm);
      font-family: inherit;
      transition: border-color 150ms, box-shadow 150ms;
      outline: none;
      box-sizing: border-box;
    }
    .dfp-input::placeholder {
      color: rgb(var(--color-placeholder));
      font-style: italic;
    }
    .dfp-input:focus {
      border-color: rgb(var(--color-focus-ring));
      box-shadow: 0 0 0 3px rgba(var(--color-focus-ring), 0.15);
    }
    .dfp-input:disabled { opacity: 0.5; cursor: not-allowed; }

    /* Submit */
    .dfp-submit {
      width: 100%;
      padding: 12px;
      background: rgb(var(--color-primary));
      color: #ffffff;
      border: none;
      border-radius: 7px;
      font-size: var(--font-sz-base);
      font-weight: 600;
      font-family: inherit;
      cursor: pointer;
      transition: background 150ms;
      letter-spacing: 0.01em;
      margin-bottom: 16px;
    }
    .dfp-submit:hover:not(:disabled) { background: rgb(var(--color-primary-hover)); }
    .dfp-submit:disabled { opacity: 0.5; cursor: not-allowed; }

    /* Back link */
    .dfp-back {
      display: block;
      text-align: center;
      font-size: var(--font-sz-sm);
      color: rgb(var(--color-text-muted));
      cursor: pointer;
      background: none;
      border: none;
      font-family: inherit;
      padding: 0;
      width: 100%;
    }
    .dfp-back:hover { color: rgb(var(--color-text)); text-decoration: underline; }

    /* Success state */
    .dfp-success-icon {
      width: 48px;
      height: 48px;
      border-radius: 50%;
      background: rgba(var(--color-success-rgb, var(--color-primary)), 0.1);
      display: flex;
      align-items: center;
      justify-content: center;
      margin: 0 auto 20px;
      font-size: 22px;
    }
    .dfp-success-title {
      font-size: var(--font-sz-lg);
      font-weight: 700;
      color: rgb(var(--color-text));
      text-align: center;
      margin-bottom: 10px;
    }
    .dfp-success-body {
      font-size: var(--font-sz-sm);
      color: rgb(var(--color-text-muted));
      text-align: center;
      line-height: 1.6;
      margin-bottom: 28px;
    }
    .dfp-success-body strong {
      color: rgb(var(--color-text));
      font-weight: 600;
    }
  `;

  return (
    <>
      <style>{css}</style>
      <div className="dfp-root">
        <div className="dfp-card">

          {/* Product identity */}
          <div className="dfp-identity">
            <div className="dfp-identity-brand">data-fit LLC</div>
            <div className="dfp-identity-name">DecisionFlow</div>
          </div>

          {submitted ? (
            /* ── Success state ── */
            <>
              <div className="dfp-success-icon" aria-hidden="true">✉</div>
              <div className="dfp-success-title">Check your inbox</div>
              <div className="dfp-success-body">
                If <strong>{email}</strong> is registered, you'll receive a
                password reset link shortly. The link expires in{" "}
                <strong>1 hour</strong>.
                <br /><br />
                Check your spam folder if it doesn't arrive within a few minutes.
              </div>
              <button
                className="dfp-submit"
                onClick={() => navigate("/login")}
              >
                Back to sign in
              </button>
            </>
          ) : (
            /* ── Form ── */
            <>
              <div className="dfp-header">
                <div className="dfp-title">Reset your password</div>
                <div className="dfp-subtitle">
                  Enter your email and workspace URL to receive a reset link.
                </div>
              </div>

              <form onSubmit={handleSubmit} noValidate>
                <div className="dfp-field">
                  <label className="dfp-label" htmlFor="dfp-email">
                    Email address
                  </label>
                  <input
                    id="dfp-email"
                    className="dfp-input"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    autoComplete="username"
                    autoFocus
                    required
                    disabled={submitting}
                    placeholder="you@company.com"
                  />
                </div>

                <div className="dfp-field">
                  <label className="dfp-label" htmlFor="dfp-slug">
                    Workspace URL
                  </label>
                  <input
                    id="dfp-slug"
                    className="dfp-input"
                    type="text"
                    value={tenantSlug}
                    onChange={(e) => setTenantSlug(e.target.value)}
                    autoComplete="off"
                    required
                    disabled={submitting}
                    placeholder="your-company"
                  />
                </div>

                <button
                  className="dfp-submit"
                  type="submit"
                  disabled={submitting || !email.trim() || !tenantSlug.trim()}
                >
                  {submitting ? "Sending…" : "Send reset link"}
                </button>
              </form>

              <button
                className="dfp-back"
                onClick={() => navigate("/login")}
                type="button"
              >
                ← Back to sign in
              </button>
            </>
          )}

        </div>
      </div>
    </>
  );
}
